import type { AiFieldRunOperation } from '@databases/analytics';
import type {
  AiSummaryFieldConfig,
  AiSummaryOutputFormat
} from '@databases/types/database';

type AiSummaryPromptContext = {
  targetFieldName: string;
  config: AiSummaryFieldConfig;
};

export type AiSummaryFieldRunOperation = AiFieldRunOperation;

type AiSummaryPromptBuilder = (context: AiSummaryPromptContext) => string;

const WRITING_STYLE_GUIDANCE =
  'Use clear, everyday words that a fourth-grade reader can understand. Prefer active voice and short, direct sentences. Avoid jargon, formal or complex wording, and uncommon -ing words used before nouns; rewrite them as simple verb phrases.';

const buildOutputFormatGuidance = (outputFormat: AiSummaryOutputFormat): string => {
  switch (outputFormat) {
    case 'one-liner':
      return 'Format the result as one clear sentence of roughly 20–35 words. Lead with the subject and its main outcome or takeaway; include another fact only when it is needed for accuracy. If the source is sparse, use a shorter useful sentence rather than stretching it.';
    case 'bullet-points':
      return 'Format the result as a compact list of no more than 3 short bullets, ordered by importance. Put the central takeaway first and give each distinct idea its own bullet. Each bullet should normally be one short sentence; aim for approximately 60 words or fewer across all bullets, but prioritize accuracy over the exact word count. Use fewer bullets when the source supports fewer facts, and do not add filler, an introduction, or a closing line.';
    case 'paragraph':
      return 'Format the result as one brief paragraph, usually 2–3 short sentences and about 40–60 words. Start with the subject and key takeaway, then add only the context needed to understand it. Use one sentence when the available source material cannot support more detail.';
    default: {
      const unhandledOutputFormat: never = outputFormat;
      throw new Error(`Unhandled AI Summary output format: ${unhandledOutputFormat}`);
    }
  }
};

const buildSharedGenerateInstructions = (targetFieldName: string) => [
  'For each included row, use all meaningful information from the other visible fields. Linked-resource fields are optional enrichment and are not required to generate a summary.',
  "If a linked-resource field is empty, unavailable, or cannot be hydrated, continue using the row's other visible fields. Do not leave the target value blank solely because a linked resource is missing.",
  'Leave the target value blank only when all other visible fields in that row contain no meaningful human-readable information.',
  `Only edit the "${targetFieldName}" field. Do not emit add_field metadata or create, remove, rename, or reconfigure fields; add or remove rows; edit views; or change any other field values.`,
  'Return the edits using the traditional AIFC Confluence database update CSV response format.',
  `CRITICAL: Ensure that the summary generated for ${targetFieldName} is wrapped in quotes and is a valid string value.`
];

const buildInitialGenerationPrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Canvas has already created the new AI Summary field named "${targetFieldName}" and included it in the database context as an existing column. It is expected to be empty.`,
    `Populate that exact existing "${targetFieldName}" column for every existing row. Do not add another field or column.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName)
  ].join('\n\n');

const buildRefreshEntireColumnPrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Regenerate the existing AI Summary field named "${targetFieldName}". It is already present in the database context and may contain existing values.`,
    `Replace only that exact "${targetFieldName}" column's value for every existing row, including rows that already have a value. Do not add another field or column.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName)
  ].join('\n\n');

const buildRefreshEmptyEntriesPrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Fill the existing AI Summary field named "${targetFieldName}" only for the provided rows, where its current value is empty.`,
    `Do not replace any existing "${targetFieldName}" values. Do not add another field or column.`,
    `For each row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName)
  ].join('\n\n');

const buildSingleEntryGeneratePrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Edit only the existing "${targetFieldName}" AI Summary value for the single row included in the database context.`,
    `For the included row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName),
    'Use exactly one edit_entry operation with the existing entry_index value.'
  ].join('\n\n');

const buildMultipleEntryGeneratePrompt: AiSummaryPromptBuilder = ({
  targetFieldName,
  config
}) =>
  [
    `Edit only the existing "${targetFieldName}" AI Summary values for the rows included in the database context.`,
    `For each included row, summarize the meaningful information from the other visible fields using the "${config.outputFormat}" output format.`,
    ...buildSharedGenerateInstructions(targetFieldName),
    'Use one edit_entry operation for each included row, using its existing entry_index value.'
  ].join('\n\n');

const promptBuilders = {
  'initial-generation': buildInitialGenerationPrompt,
  'refresh-entire-column': buildRefreshEntireColumnPrompt,
  'refresh-empty-entries': buildRefreshEmptyEntriesPrompt,
  'single-entry-generate': buildSingleEntryGeneratePrompt,
  'multiple-entry-generate': buildMultipleEntryGeneratePrompt
} satisfies Record<AiSummaryFieldRunOperation, AiSummaryPromptBuilder>;

export const buildAiSummaryPrompt = ({
  operation,
  targetFieldName,
  config
}: AiSummaryPromptContext & { operation: AiSummaryFieldRunOperation }): string =>
  [
    promptBuilders[operation]({ targetFieldName, config }),
    WRITING_STYLE_GUIDANCE,
    buildOutputFormatGuidance(config.outputFormat)
  ].join('\n\n');
